'use client';

import { usePathname } from 'next/navigation';
import { useSession, getSession } from 'next-auth/react';
import { env } from 'next-runtime-env';
import { useState, useEffect, useCallback, useRef } from 'react';
import { clientSignOut } from '@/lib/clientSignOut';

// Time in seconds before expiry to show the warning
const WARNING_BEFORE_TIMEOUT_SECONDS = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_WARNING_SECONDS') || '300', 10);

const CHECK_INTERVAL = 1000; // 1 second
const SESSION_TIMEOUT_ATLAS = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_ATLAS') || '86400', 10);
const SESSION_TIMEOUT_EXTERNAL = parseInt(env('NEXT_PUBLIC_SESSION_TIMEOUT_EXTERNAL') || '7200', 10);

interface SessionTimeoutAlertProps {
  className?: string;
}

export const SessionTimeoutAlert = ({ className }: SessionTimeoutAlertProps) => {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [lastReset, setLastReset] = useState(Date.now());
  // --- BroadcastChannel setup ---
  const channelRef = useRef<BroadcastChannel | null>(null);
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('session-sync');
  }

  // Debounce for activity reset
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store the cleanup function in a ref so it can be called from anywhere
  const cleanupActivityListeners = useRef<() => void>(() => {});

  useEffect(() => {
    const handleActivity = () => {
      if (showWarning) return; // Do not reset timer if warning is showing
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = setTimeout(() => {
        const now = Date.now();
        setLastReset(now);
        localStorage.setItem('sessionLastReset', String(now));
        channelRef.current?.postMessage({ type: 'reset', timestamp: now });
        setShowWarning(false);
        // (this block will not run if showWarning is true)
      }, 200); // Debounce to avoid excessive resets
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    cleanupActivityListeners.current = () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
    return cleanupActivityListeners.current;
  }, [showWarning]);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    // Remove activity listeners immediately
    cleanupActivityListeners.current();
    try {
      channelRef.current?.postMessage({ type: 'logout' });
      await clientSignOut();
    } catch (error) {
      // fallback below
    } finally {
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      }
    }
  }, [isSigningOut]);

  // Store interval id in a ref so we can clear/restart it
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const startTimerInterval = useCallback(() => {
    if (!session) return;
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    intervalIdRef.current = setInterval(() => {
      if (!session) return;
      const now = Date.now();
      // Always re-read lastReset from localStorage for perfect sync
      const storedReset = Number(localStorage.getItem('sessionLastReset')) || lastReset;
      const isAtlasUser = session?.user?.email?.endsWith('@atlas-sp.com') || false;
      const sessionTimeoutValue = isAtlasUser ? SESSION_TIMEOUT_ATLAS : SESSION_TIMEOUT_EXTERNAL;
      const sessionCookieExpiry = new Date(session.expires).getTime();
      let timeRemaining: number;
      const inactivityTimeRemaining = storedReset + sessionTimeoutValue * 1000 - now;
      const cookieTimeRemaining = sessionCookieExpiry - now;
      timeRemaining = Math.floor(Math.min(inactivityTimeRemaining, cookieTimeRemaining) / 1000);

      if (timeRemaining <= WARNING_BEFORE_TIMEOUT_SECONDS && timeRemaining >= 0) {
        setShowWarning(true);
        setSecondsLeft(timeRemaining);
        if (timeRemaining === 0 && !isSigningOut) {
          setTimeout(() => handleSignOut(), 0);
        }
      } else if (timeRemaining < 0 && !isSigningOut) {
        handleSignOut();
      } else {
        setShowWarning(false);
      }
    }, CHECK_INTERVAL);
  }, [session, lastReset, isSigningOut, handleSignOut, showWarning]);

  // Start timer on mount and whenever session/status changes
  useEffect(() => {
    if (!session || status !== 'authenticated') return;
    startTimerInterval();
    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [session, status, startTimerInterval]);

  // Whenever session becomes authenticated, treat as Keep Session Active
  useEffect(() => {
    if (session && status === 'authenticated') {
      const now = Date.now();
      setLastReset(now);
      localStorage.setItem('sessionLastReset', String(now));
      channelRef.current?.postMessage({ type: 'reset', timestamp: now });
      setShowWarning(false);
      // Restart timer interval for perfect sync
      startTimerInterval();
    }
  }, [session, status]);

  // Listen for messages from other tabs
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;
    channel.onmessage = event => {
      if (event.data?.type === 'reset') {
        setLastReset(event.data.timestamp);
        localStorage.setItem('sessionLastReset', String(event.data.timestamp));
        setShowWarning(false);
        // Restart timer interval for perfect sync
        startTimerInterval();
      }
      if (event.data?.type === 'logout') {
        handleSignOut();
      }
    };
    // No cleanup needed here; channel is closed on unmount
  }, [handleSignOut, startTimerInterval]);

  // On tab wake, check session expiry
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && session && status === 'authenticated') {
        const sessionCookieExpiry = new Date(session.expires).getTime();
        if (Date.now() > sessionCookieExpiry) {
          handleSignOut();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [session, status, handleSignOut]);

  const resetSession = useCallback(async () => {
    try {
      const now = Date.now();
      setLastReset(now); // Reset inactivity timer
      localStorage.setItem('sessionLastReset', String(now));
      setShowWarning(false);
      channelRef.current?.postMessage({ type: 'reset', timestamp: now });
      await fetch('/api/auth/session', { method: 'GET' });
      await getSession();
    } catch (error) {
      await handleSignOut();
    }
  }, [handleSignOut]);

  // --- Compute which timer is expiring first ---
  let canKeepSessionActive = false;
  if (session) {
    const now = Date.now();
    const storedReset = Number(localStorage.getItem('sessionLastReset')) || lastReset;
    const isAtlasUser = session?.user?.email?.endsWith('@atlas-sp.com') || false;
    const sessionTimeoutValue = isAtlasUser ? SESSION_TIMEOUT_ATLAS : SESSION_TIMEOUT_EXTERNAL;
    const sessionCookieExpiry = new Date(session.expires).getTime();
    const hasInactivityTimeout = Boolean(
      (isAtlasUser && process.env.NEXT_PUBLIC_SESSION_TIMEOUT_ATLAS) ||
        (!isAtlasUser && process.env.NEXT_PUBLIC_SESSION_TIMEOUT_EXTERNAL),
    );
    if (hasInactivityTimeout) {
      const inactivityTimeRemaining = storedReset + sessionTimeoutValue * 1000 - now;
      const cookieTimeRemaining = sessionCookieExpiry - now;
      canKeepSessionActive = inactivityTimeRemaining <= cookieTimeRemaining;
    }
  }

  if (pathname?.startsWith('/auth')) return null;

  if (!showWarning) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 rounded-md border border-amber-300 bg-amber-50 p-4 shadow-lg ${className}`}>
      <h3 className="text-lg font-semibold text-amber-800">Session Timeout Warning</h3>
      <p className="mb-2 text-amber-700">Your session will expire in {secondsLeft} seconds.</p>
      <div className="flex gap-2">
        {canKeepSessionActive && secondsLeft > 0 && (
          <button
            onClick={resetSession}
            className="rounded bg-blue-600 px-3 py-1 font-medium text-white hover:bg-blue-700">
            Keep Session Active
          </button>
        )}
        <button
          onClick={handleSignOut}
          className="rounded bg-gray-200 px-3 py-1 font-medium text-gray-800 hover:bg-gray-300">
          Logout Now
        </button>
      </div>
    </div>
  );
};

export default SessionTimeoutAlert;
